import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { uploadFileToS3 } from '../../api/commonAPI/awsUploadAPI';
import { S3_FOLDERS } from '../../config/s3Config';
import {
    Wallet, Landmark, Layers, Plus, Pencil, X, Loader2, ChevronDown,
    CheckCircle2, PlusCircle, Trash2,
} from 'lucide-react';

import {
    fetchCCTypes, fetchCCFinancialYears, fetchCCSubTypes, fetchWorkflowStatus, fetchCostCentersForAssign,
    fetchCCGrid, submitAssignCCBudget, submitAmendCCBudget, fetchCCBalance, fetchOtherCCs,
    fetchCCsForDCA, fetchDCAGrid, fetchCCDetailsById, fetchBudgetDCADetails,
    submitAssignDCABudget, submitSingleDCAAmend, fetchPendingDCAAmendments, fetchUnAmendedDCAs,
    submitNewDCAUnderCC, submitMultipleDCAAmend, deleteSingleDCAAmendment,
    clearCCSaveResult, clearDCASaveResult, clearSingleAmendResult, clearNewDCAResult, clearConfirmAmendResult,
    clearCCBalance, clearCCDetailsById, resetAssignBudget,
    selectAssignBudget,
} from '../../slices/budgetSlice/assignBudgetSlice';

// ── Shared primitives ──────────────────────────────────────────────────────────
const inputCls = 'w-full px-3 py-2 border-2 rounded-xl text-sm bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:border-indigo-500 focus:ring-indigo-100 dark:focus:ring-indigo-900/30 transition-all disabled:opacity-60';
const selectCls = inputCls + ' appearance-none pr-9 cursor-pointer';

const Label = ({ children, required }) => (
    <label className="block text-xs font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-1">
        {children}{required && <span className="text-rose-500 ml-0.5">*</span>}
    </label>
);

const SelectWrap = ({ children, loading }) => (
    <div className="relative">
        {children}
        <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
            {loading ? <Loader2 className="h-4 w-4 text-indigo-500 animate-spin" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
        </div>
    </div>
);

const Btn = ({ onClick, disabled, loading, variant = 'primary', size = 'md', children, type = 'button' }) => {
    const base = 'inline-flex items-center gap-2 font-semibold rounded-xl transition-all disabled:opacity-60 disabled:cursor-not-allowed';
    const sizes = { sm: 'px-3 py-1.5 text-xs', md: 'px-4 py-2 text-sm' };
    const variants = {
        primary: 'bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm',
        secondary: 'bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:border-indigo-400',
        danger: 'bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-700 text-rose-600 dark:text-rose-400 hover:bg-rose-100',
    };
    return (
        <button type={type} onClick={onClick} disabled={disabled || loading} className={`${base} ${sizes[size]} ${variants[variant]}`}>
            {loading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {children}
        </button>
    );
};

const Badge = ({ children, color = 'gray' }) => {
    const colors = {
        green: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-700',
        red: 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/20 dark:text-rose-400 dark:border-rose-700',
        amber: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-700',
        gray: 'bg-gray-50 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700',
    };
    return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border ${colors[color] || colors.gray}`}>{children}</span>;
};

const Card = ({ children, className = '' }) => (
    <div className={`bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm ${className}`}>{children}</div>
);

const EmptyState = ({ message }) => <div className="py-10 text-center text-gray-400 dark:text-gray-500 text-sm">{message}</div>;

const Modal = ({ title, onClose, children, wide }) => (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
        <div className={`bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full ${wide ? 'max-w-4xl' : 'max-w-2xl'} max-h-[90vh] overflow-y-auto`}>
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-indigo-600 to-violet-600 rounded-t-2xl sticky top-0 z-10">
                <h2 className="text-sm font-bold text-white">{title}</h2>
                <button onClick={onClose} className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"><X className="w-4 h-4 text-white" /></button>
            </div>
            <div className="p-5">{children}</div>
        </div>
    </div>
);

// Legacy accepts PDF only ("Invalid Upload File. Upload only PDF File").
const pickPdf = (e) => {
    const f = e.target.files?.[0] || null;
    if (f && f.name.split('.').pop().toLowerCase() !== 'pdf') {
        toast.error('Invalid Upload File. Upload only PDF File');
        e.target.value = '';
        return null;
    }
    return f;
};

const joinIds = (arr) => (arr && arr.length ? arr.join(',') + ',' : '');
const fmt = (v) => { const n = parseFloat(v); return isNaN(n) ? '0.00' : n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); };

// ═════════════════════════════════════════════════════════════════════════════
// ASSIGN CC BUDGET MODAL
// ═════════════════════════════════════════════════════════════════════════════

const AssignCCBudgetModal = ({ onClose, currentUser, roleId, uid, ccTypes, ccFinancialYears, ccSubTypes }) => {
    const dispatch = useDispatch();
    const { costCentersForAssign, costCentersForAssignLoading, ccSaveLoading, ccSaveResult, ccSaveError } = useSelector(selectAssignBudget);
    const [ccTypeId, setCcTypeId] = useState('');
    const [subType, setSubType] = useState('');
    const [year, setYear] = useState('');
    const [ccCode, setCcCode] = useState('');
    const [amount, setAmount] = useState('');
    const [remarks, setRemarks] = useState('');
    // Legacy: Performing CCs must attach the "Approved budget for execution" PDF, uploaded under the
    // file name spAssignCCBudget returns ("Submitted,<ExecutionApprBudget_...pdf>").
    const [execFile, setExecFile] = useState(null);

    const ccTypeDesc = ccTypes.find((t) => String(t.CCTypeID) === String(ccTypeId))?.CCTypeDescription || '';
    const isPerforming = ccTypeDesc === 'Performing';

    useEffect(() => {
        if (ccSaveResult) {
            const fileKey = (String(ccSaveResult).split(',')[1] || '').trim();
            const file = isPerforming ? execFile : null;
            (async () => {
                if (file && fileKey) {
                    try { await uploadFileToS3(file, S3_FOLDERS.COST_CENTER_BUDGET, fileKey); }
                    catch (e) { toast.warning(`Budget assigned, but document upload failed: ${e?.Message || e?.message || e}`); }
                }
                toast.success('Cost centre budget assigned successfully.');
                dispatch(clearCCSaveResult()); onClose();
            })();
        }
        if (ccSaveError) { toast.error(ccSaveError); dispatch(clearCCSaveResult()); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ccSaveResult, ccSaveError]);

    const handleCcTypeChange = (val) => {
        setCcTypeId(val); setSubType(''); setYear(''); setCcCode('');
        const desc = ccTypes.find((t) => String(t.CCTypeID) === String(val))?.CCTypeDescription || '';
        if (desc && desc !== 'Performing') dispatch(fetchCostCentersForAssign({ CCTypeID: val, SubType: '' }));
    };
    const handleSubTypeChange = (val) => {
        setSubType(val); setCcCode('');
        if (isPerforming) dispatch(fetchCostCentersForAssign({ CCTypeID: ccTypeId, SubType: val }));
    };

    const handleSubmit = () => {
        if (!ccTypeId) { toast.warning('Select a Cost Centre Type.'); return; }
        if (isPerforming && !subType) { toast.warning('Select a Sub Type.'); return; }
        if (!isPerforming && !year) { toast.warning('Select a Year.'); return; }
        if (!ccCode) { toast.warning('Select a Cost Centre.'); return; }
        if (!amount || parseFloat(amount) <= 0) { toast.warning('Enter a valid Amount.'); return; }
        if (!remarks.trim()) { toast.warning('Enter Remarks.'); return; }
        if (isPerforming && !execFile) { toast.warning('Upload Approved budget for execution.'); return; }

        dispatch(submitAssignCCBudget({
            CCTypeID: parseInt(ccTypeId, 10),
            CCType: ccTypeDesc,
            SubType: isPerforming ? subType : '',
            CostCenter: ccCode,
            Year: isPerforming ? '' : year,
            Remarks: remarks,
            Amount: parseFloat(amount),
            Createdby: currentUser,
            RoleId: roleId,
            UID: uid,
        }));
    };

    return (
        <Modal title="Assign Cost Centre Budget" onClose={onClose}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <Label required>Cost Centre Type</Label>
                    <SelectWrap>
                        <select className={selectCls} value={ccTypeId} onChange={(e) => handleCcTypeChange(e.target.value)} disabled={ccSaveLoading}>
                            <option value="">-Please Select-</option>
                            {ccTypes.map((t) => <option key={t.CCTypeID} value={t.CCTypeID}>{t.CCTypeDescription}</option>)}
                        </select>
                    </SelectWrap>
                </div>
                {isPerforming ? (
                    <div>
                        <Label required>Sub Type</Label>
                        <SelectWrap>
                            <select className={selectCls} value={subType} onChange={(e) => handleSubTypeChange(e.target.value)} disabled={ccSaveLoading || !ccTypeId}>
                                <option value="">-Please Select-</option>
                                {ccSubTypes.map((s, i) => <option key={i} value={s.CC_SubType}>{s.CC_SubType}</option>)}
                            </select>
                        </SelectWrap>
                    </div>
                ) : (
                    <div>
                        <Label required>Year</Label>
                        <SelectWrap>
                            <select className={selectCls} value={year} onChange={(e) => setYear(e.target.value)} disabled={ccSaveLoading || !ccTypeId}>
                                <option value="">-Please Select-</option>
                                {ccFinancialYears.map((y, i) => <option key={i} value={y.CC_FinYear}>{y.CC_FinYear}</option>)}
                            </select>
                        </SelectWrap>
                    </div>
                )}
                <div>
                    <Label required>Cost Centre</Label>
                    <SelectWrap loading={costCentersForAssignLoading}>
                        <select className={selectCls} value={ccCode} onChange={(e) => setCcCode(e.target.value)} disabled={ccSaveLoading || (isPerforming ? !subType : !year)}>
                            <option value="">-Please Select-</option>
                            {costCentersForAssign.map((c) => <option key={c.CC_Id} value={c.CC_Code}>{c.CC_Name}</option>)}
                        </select>
                    </SelectWrap>
                </div>
                <div>
                    <Label required>Amount</Label>
                    <input className={inputCls} value={amount} onChange={(e) => setAmount(e.target.value)} disabled={ccSaveLoading} inputMode="decimal" />
                </div>
                <div className="md:col-span-2">
                    <Label required>Remarks</Label>
                    <textarea rows={2} className={`${inputCls} resize-none`} value={remarks} onChange={(e) => setRemarks(e.target.value)} disabled={ccSaveLoading} />
                </div>
                {isPerforming && (
                    <div className="md:col-span-2">
                        <Label required>Approved budget for execution (PDF)</Label>
                        <input type="file" accept="application/pdf,.pdf" className={inputCls} onChange={(e) => setExecFile(pickPdf(e))} disabled={ccSaveLoading} />
                    </div>
                )}
            </div>
            <div className="flex justify-end gap-2 mt-5">
                <Btn variant="secondary" onClick={onClose} disabled={ccSaveLoading}>Cancel</Btn>
                <Btn onClick={handleSubmit} loading={ccSaveLoading}>Submit</Btn>
            </div>
        </Modal>
    );
};

// ═════════════════════════════════════════════════════════════════════════════
// AMEND CC BUDGET MODAL
// ═════════════════════════════════════════════════════════════════════════════

const AMEND_TYPES = ['Add', 'Substract'];

const AmendCCBudgetModal = ({ row, onClose, currentUser, roleId, uid, ccTypes }) => {
    const dispatch = useDispatch();
    const { ccSaveLoading, ccSaveResult, ccSaveError, otherCCs, otherCCsLoading } = useSelector(selectAssignBudget);
    const [amendType, setAmendType] = useState('');
    const [amount, setAmount] = useState('');
    const [remarks, setRemarks] = useState('');
    const [fromOtherCC, setFromOtherCC] = useState(false);
    const [otherCcType, setOtherCcType] = useState('');
    const [otherCcCode, setOtherCcCode] = useState('');
    // Legacy: amending a Performing CC requires a PDF (Filechk='Yes'); spAmendCCBudget returns "Submited$<file>".
    const [amendFile, setAmendFile] = useState(null);
    const isPerformingRow = row.CC_Type === 'Performing';

    useEffect(() => {
        if (ccSaveResult) {
            const fileKey = (String(ccSaveResult).split('$')[1] || '').trim();
            const file = amendFile;
            (async () => {
                if (file && fileKey) {
                    try { await uploadFileToS3(file, S3_FOLDERS.CC_BUDGET_AMENDMENT, fileKey); }
                    catch (e) { toast.warning(`Amendment saved, but document upload failed: ${e?.Message || e?.message || e}`); }
                }
                toast.success('Amendment saved successfully.');
                dispatch(clearCCSaveResult()); onClose();
            })();
        }
        if (ccSaveError) { toast.error(ccSaveError); dispatch(clearCCSaveResult()); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ccSaveResult, ccSaveError]);

    const balance = parseFloat(row.BalanceBudget) || 0;
    const otherCcBalance = parseFloat(otherCCs.find((c) => c.CCCode === otherCcCode)?.Value) || 0;
    // spGetOtherBudgetCCs returns CCCode as display text "CODE ,  Avaliable Balance:- X[ , FYyear]".
    // Legacy sends only the code, and for Capital/Non-Performing the debited CC's own FY year.
    const otherParts = String(otherCcCode || '').split(',');
    const otherCcPlainCode = (otherParts[0] || '').trim();
    const otherCcYear = otherCcType === 'Performing' ? null : ((otherParts[2] || '').trim() || null);

    const handleOtherCcTypeChange = (val) => {
        setOtherCcType(val); setOtherCcCode('');
        if (val) dispatch(fetchOtherCCs({ CCCode: row.CC_Code, CCTypeval: val, CCYear: row.FYyear }));
    };

    const handleSubmit = () => {
        if (!amendType) { toast.warning('Select an Amend Type.'); return; }
        const amt = parseFloat(amount);
        if (!amount || isNaN(amt) || amt <= 0) { toast.warning('Enter a valid Amount.'); return; }
        if (amendType === 'Substract' && amt > balance) { toast.warning('Subtract Amount is greater than the Balance.'); return; }
        if (amendType === 'Add' && fromOtherCC) {
            if (!otherCcType) { toast.warning('Select the other Cost Centre Type.'); return; }
            if (!otherCcCode) { toast.warning('Select the other Cost Centre.'); return; }
            if (amt > otherCcBalance) { toast.warning('Amount exceeds the selected Cost Centre\'s balance.'); return; }
        }
        if (!remarks.trim()) { toast.warning('Enter Remarks.'); return; }
        if (isPerformingRow && !amendFile) { toast.warning('Upload Docs.'); return; }

        const newBudget = amendType === 'Add' ? (parseFloat(row.BudgetValue) || 0) + amt : (parseFloat(row.BudgetValue) || 0) - amt;
        const newBalance = amendType === 'Add' ? balance + amt : balance - amt;

        dispatch(submitAmendCCBudget({
            CCCode: row.CC_Code,
            AmendedValue: amt,
            AmendmentType: amendType,
            BudgetId: row.CCBudgetId,
            NewBudget: newBudget,
            NewBudgetBalance: newBalance,
            CreatedBy: currentUser,
            UID: uid,
            RoleId: roleId,
            FYYear: row.FYyear,
            Remarks: remarks,
            OtherCCCodechk: fromOtherCC ? 'Yes' : 'No',
            OtherCCCode: fromOtherCC ? otherCcPlainCode : '',
            DebitCCType: fromOtherCC ? otherCcType : '',
            CapitalCCYear: fromOtherCC ? otherCcYear : null,
            Filechk: amendFile ? 'Yes' : 'No',
        }));
    };

    return (
        <Modal title={`Amend Budget — ${row.CC_Code}`} onClose={onClose}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 text-xs">
                <div className="bg-gray-50 dark:bg-gray-900/40 rounded-xl p-3"><p className="text-gray-400 uppercase font-bold text-[10px]">CC Name</p><p className="font-semibold text-gray-800 dark:text-gray-100">{row.CC_Name}</p></div>
                <div className="bg-gray-50 dark:bg-gray-900/40 rounded-xl p-3"><p className="text-gray-400 uppercase font-bold text-[10px]">Budget Value</p><p className="font-semibold text-gray-800 dark:text-gray-100">{fmt(row.BudgetValue)}</p></div>
                <div className="bg-gray-50 dark:bg-gray-900/40 rounded-xl p-3"><p className="text-gray-400 uppercase font-bold text-[10px]">Balance</p><p className="font-semibold text-indigo-600">{fmt(row.BalanceBudget)}</p></div>
                <div className="bg-gray-50 dark:bg-gray-900/40 rounded-xl p-3"><p className="text-gray-400 uppercase font-bold text-[10px]">Financial Year</p><p className="font-semibold text-gray-800 dark:text-gray-100">{row.FYyear}</p></div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <Label required>Amend Type</Label>
                    <SelectWrap>
                        <select className={selectCls} value={amendType} onChange={(e) => { setAmendType(e.target.value); setFromOtherCC(false); }} disabled={ccSaveLoading}>
                            <option value="">-Select-</option>
                            {AMEND_TYPES.map((t) => <option key={t} value={t}>{t === 'Substract' ? 'Subtract' : t}</option>)}
                        </select>
                    </SelectWrap>
                </div>
                <div>
                    <Label required>Amount</Label>
                    <input className={inputCls} value={amount} onChange={(e) => setAmount(e.target.value)} disabled={ccSaveLoading} inputMode="decimal" />
                </div>

                {amendType === 'Add' && (
                    <div className="md:col-span-2">
                        <label className="flex items-center gap-2 text-xs font-semibold text-gray-600 dark:text-gray-300">
                            <input type="checkbox" checked={fromOtherCC} onChange={(e) => setFromOtherCC(e.target.checked)} disabled={ccSaveLoading} />
                            Add Budget from another Cost Centre?
                        </label>
                    </div>
                )}
                {amendType === 'Add' && fromOtherCC && (
                    <>
                        <div>
                            <Label required>Other CC Type</Label>
                            <SelectWrap>
                                <select className={selectCls} value={otherCcType} onChange={(e) => handleOtherCcTypeChange(e.target.value)} disabled={ccSaveLoading}>
                                    <option value="">-Select-</option>
                                    {/* Legacy offers only these two debit types */}
                                    {['Performing', 'Capital'].map((t) => <option key={t} value={t}>{t}</option>)}
                                </select>
                            </SelectWrap>
                        </div>
                        <div>
                            <Label required>Other Cost Centre</Label>
                            <SelectWrap loading={otherCCsLoading}>
                                <select className={selectCls} value={otherCcCode} onChange={(e) => setOtherCcCode(e.target.value)} disabled={ccSaveLoading || !otherCcType}>
                                    <option value="">-Select-</option>
                                    {otherCCs.map((c) => <option key={c.CCCode} value={c.CCCode}>{c.CCCode}</option>)}
                                </select>
                            </SelectWrap>
                        </div>
                    </>
                )}

                <div className="md:col-span-2">
                    <Label required>Remarks</Label>
                    <textarea rows={2} className={`${inputCls} resize-none`} value={remarks} onChange={(e) => setRemarks(e.target.value)} disabled={ccSaveLoading} />
                </div>
                {isPerformingRow && (
                    <div className="md:col-span-2">
                        <Label required>Upload Docs (PDF)</Label>
                        <input type="file" accept="application/pdf,.pdf" className={inputCls} onChange={(e) => setAmendFile(pickPdf(e))} disabled={ccSaveLoading} />
                    </div>
                )}
            </div>
            <div className="flex justify-end gap-2 mt-5">
                <Btn variant="secondary" onClick={onClose} disabled={ccSaveLoading}>Cancel</Btn>
                <Btn onClick={handleSubmit} loading={ccSaveLoading}>Submit</Btn>
            </div>
        </Modal>
    );
};

// ═════════════════════════════════════════════════════════════════════════════
// CC BUDGET TAB
// ═════════════════════════════════════════════════════════════════════════════

const CCBudgetTab = ({ currentUser, roleId, uid, ccTypes, ccTypesLoading, ccFinancialYears, ccSubTypes }) => {
    const dispatch = useDispatch();
    const { ccGrid, ccGridLoading, ccGridError } = useSelector(selectAssignBudget);
    const [ccTypeId, setCcTypeId] = useState('');
    const [subType, setSubType] = useState('');
    const [year, setYear] = useState('');
    const [showAssign, setShowAssign] = useState(false);
    const [amendRow, setAmendRow] = useState(null);

    const ccTypeDesc = ccTypes.find((t) => String(t.CCTypeID) === String(ccTypeId))?.CCTypeDescription || '';
    const isPerforming = ccTypeDesc === 'Performing';

    const runView = () => {
        dispatch(fetchCCGrid({ Roleid: roleId, FnYear: isPerforming ? '' : year, CCSubType: isPerforming ? subType : '', CCType: ccTypeDesc }));
    };

    return (
        <div className="space-y-4">
            <Card>
                <div className="p-4 flex flex-wrap items-end gap-3">
                    <div className="w-48">
                        <Label>Cost Centre Type</Label>
                        <SelectWrap loading={ccTypesLoading}>
                            <select className={selectCls} value={ccTypeId} onChange={(e) => { setCcTypeId(e.target.value); setSubType(''); setYear(''); }}>
                                <option value="">All Types</option>
                                {ccTypes.map((t) => <option key={t.CCTypeID} value={t.CCTypeID}>{t.CCTypeDescription}</option>)}
                            </select>
                        </SelectWrap>
                    </div>
                    {isPerforming ? (
                        <div className="w-48">
                            <Label>Sub Type</Label>
                            <SelectWrap>
                                <select className={selectCls} value={subType} onChange={(e) => setSubType(e.target.value)}>
                                    <option value="">All Sub Types</option>
                                    {ccSubTypes.map((s, i) => <option key={i} value={s.CC_SubType}>{s.CC_SubType}</option>)}
                                </select>
                            </SelectWrap>
                        </div>
                    ) : (
                        <div className="w-48">
                            <Label>Year</Label>
                            <SelectWrap>
                                <select className={selectCls} value={year} onChange={(e) => setYear(e.target.value)}>
                                    <option value="">All Years</option>
                                    {ccFinancialYears.map((y, i) => <option key={i} value={y.CC_FinYear}>{y.CC_FinYear}</option>)}
                                </select>
                            </SelectWrap>
                        </div>
                    )}
                    <Btn onClick={runView} loading={ccGridLoading} variant="secondary">View</Btn>
                    <div className="ml-auto">
                        <Btn onClick={() => setShowAssign(true)}><Plus className="h-3.5 w-3.5" />Assign Cost Centre Budget</Btn>
                    </div>
                </div>
            </Card>

            <Card>
                {ccGridLoading ? (
                    <div className="py-14 flex justify-center"><Loader2 className="h-6 w-6 text-indigo-500 animate-spin" /></div>
                ) : ccGridError ? (
                    <div className="py-10 text-center text-sm text-rose-500">{ccGridError}</div>
                ) : ccGrid.length === 0 ? (
                    <EmptyState message="No cost centre budgets found. Adjust filters and click View." />
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-gray-50 dark:bg-gray-900/40 text-left">
                                    {['CC Code', 'CC Name', 'Type', 'Created', 'Budget Value', 'Balance', 'Status', ''].map((h) => (
                                        <th key={h} className="px-3 py-2.5 text-xs font-bold text-gray-500 uppercase tracking-wider">{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50 dark:divide-gray-700">
                                {ccGrid.map((row, i) => (
                                    <tr key={i} className="hover:bg-indigo-50/40 dark:hover:bg-indigo-900/10 transition-colors">
                                        <td className="px-3 py-2.5 font-mono text-gray-700 dark:text-gray-300">{row.CC_Code}</td>
                                        <td className="px-3 py-2.5 text-gray-800 dark:text-gray-100">{row.CC_Name}</td>
                                        <td className="px-3 py-2.5 text-gray-600 dark:text-gray-300">{row.CC_Type}</td>
                                        <td className="px-3 py-2.5 text-gray-500 text-xs">{row.CreatedDate}</td>
                                        <td className="px-3 py-2.5 text-right font-semibold">{fmt(row.BudgetValue)}</td>
                                        <td className="px-3 py-2.5 text-right font-semibold text-indigo-600">{fmt(row.BalanceBudget)}</td>
                                        <td className="px-3 py-2.5"><Badge color={row.Status === 'Approved' ? 'green' : row.Status === 'Rejected' ? 'red' : 'amber'}>{row.Status}</Badge></td>
                                        <td className="px-3 py-2.5">
                                            {row.Status === 'Approved' && (
                                                <button onClick={() => setAmendRow(row)} className="p-1.5 rounded-lg text-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-900/30" title="Amend">
                                                    <Pencil className="h-3.5 w-3.5" />
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>

            {showAssign && (
                <AssignCCBudgetModal onClose={() => setShowAssign(false)} currentUser={currentUser} roleId={roleId} uid={uid} ccTypes={ccTypes} ccFinancialYears={ccFinancialYears} ccSubTypes={ccSubTypes} />
            )}
            {amendRow && (
                <AmendCCBudgetModal row={amendRow} onClose={() => { setAmendRow(null); runView(); }} currentUser={currentUser} roleId={roleId} uid={uid} ccTypes={ccTypes} />
            )}
        </div>
    );
};

// ═════════════════════════════════════════════════════════════════════════════
// ASSIGN DCA BUDGET MODAL
// ═════════════════════════════════════════════════════════════════════════════

const AssignDCABudgetModal = ({ onClose, currentUser, roleId, ccTypes, ccFinancialYears }) => {
    const dispatch = useDispatch();
    const {
        costCentersForAssign, costCentersForAssignLoading,
        ccDetailsById, ccDetailsByIdLoading, budgetDCADetails, budgetDCADetailsLoading,
        dcaSaveLoading, dcaSaveResult, dcaSaveError,
    } = useSelector(selectAssignBudget);

    const [ccTypeId, setCcTypeId] = useState('');
    const [ccCode, setCcCode] = useState('');
    const [year, setYear] = useState('');
    const [remarks, setRemarks] = useState('');
    const [rowState, setRowState] = useState({}); // { [DCACode]: { amount, checked } }

    const ccTypeDesc = ccTypes.find((t) => String(t.CCTypeID) === String(ccTypeId))?.CCTypeDescription || '';
    const isPerforming = ccDetailsById?.CostCenterTypeName === 'Performing' || ccTypeDesc === 'Performing';

    useEffect(() => {
        if (ccTypeId) dispatch(fetchCostCentersForAssign({ CCTypeID: ccTypeId, SubType: '' }));
    }, [dispatch, ccTypeId]);

    useEffect(() => {
        if (dcaSaveResult) { toast.success('Account head budget assigned successfully.'); dispatch(clearDCASaveResult()); onClose(); }
        if (dcaSaveError) { toast.error(dcaSaveError); dispatch(clearDCASaveResult()); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dcaSaveResult, dcaSaveError]);

    const handleCcChange = (code) => {
        setCcCode(code); setRowState({});
        dispatch(clearCCDetailsById());
        const cc = costCentersForAssign.find((c) => c.CC_Code === code);
        if (cc) {
            dispatch(fetchCCDetailsById(cc.CC_Id)).then((res) => {
                const details = res.payload;
                if (details) dispatch(fetchBudgetDCADetails(details.CostCenterTypeID));
            });
        }
    };

    const checkedRows = Object.entries(rowState).filter(([, v]) => v.checked);
    const subtotal = checkedRows.reduce((s, [, v]) => s + (parseFloat(v.amount) || 0), 0);
    const ccBudget = parseFloat(ccDetailsById?.AssignedBudget) || 0;

    const updateRow = (code, patch) => setRowState((p) => ({ ...p, [code]: { ...(p[code] || { amount: '', checked: false }), ...patch } }));

    const handleSubmit = () => {
        if (!ccCode) { toast.warning('Select a Cost Centre.'); return; }
        if (checkedRows.length === 0) { toast.warning('Check at least one Account Head to assign a budget.'); return; }
        if (checkedRows.some(([, v]) => !v.amount || parseFloat(v.amount) === 0)) { toast.warning('Enter a budget amount for every checked Account Head.'); return; }
        if (subtotal > ccBudget) { toast.warning('Account Head budget total is greater than the Cost Centre budget.'); return; }
        if (!isPerforming && !year) { toast.warning('Select a Year.'); return; }
        if (!remarks.trim()) { toast.warning('Enter Remarks.'); return; }

        dispatch(submitAssignDCABudget({
            DCACode: joinIds(checkedRows.map(([code]) => code)),
            DcaAmounts: joinIds(checkedRows.map(([, v]) => v.amount)),
            CCCode: ccCode,
            FYyear: isPerforming ? '' : year,
            Remarks: remarks,
            Action: 'New',
            OldBudgetAmount: 0,
            CreatedBy: currentUser,
            RoleId: roleId,
        }));
    };

    return (
        <Modal title="Assign Account Head (DCA) Budget" onClose={onClose} wide>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div>
                    <Label required>Cost Centre Type</Label>
                    <SelectWrap>
                        <select className={selectCls} value={ccTypeId} onChange={(e) => { setCcTypeId(e.target.value); setCcCode(''); setYear(''); setRowState({}); dispatch(clearCCDetailsById()); }} disabled={dcaSaveLoading}>
                            <option value="">-Please Select-</option>
                            {ccTypes.map((t) => <option key={t.CCTypeID} value={t.CCTypeID}>{t.CCTypeDescription}</option>)}
                        </select>
                    </SelectWrap>
                </div>
                <div>
                    <Label required>Cost Centre</Label>
                    <SelectWrap loading={costCentersForAssignLoading}>
                        <select className={selectCls} value={ccCode} onChange={(e) => handleCcChange(e.target.value)} disabled={dcaSaveLoading || !ccTypeId}>
                            <option value="">-Please Select-</option>
                            {costCentersForAssign.map((c) => <option key={c.CC_Id} value={c.CC_Code}>{c.CC_Name}</option>)}
                        </select>
                    </SelectWrap>
                </div>
                {!isPerforming && (
                    <div>
                        <Label required>Year</Label>
                        <SelectWrap>
                            <select className={selectCls} value={year} onChange={(e) => setYear(e.target.value)} disabled={dcaSaveLoading || !ccCode}>
                                <option value="">-Please Select-</option>
                                {ccFinancialYears.map((y, i) => <option key={i} value={y.CC_FinYear}>{y.CC_FinYear}</option>)}
                            </select>
                        </SelectWrap>
                    </div>
                )}
            </div>

            {ccCode && (
                <div className="mb-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                    <div className="bg-gray-50 dark:bg-gray-900/40 rounded-xl p-3"><p className="text-gray-400 uppercase font-bold text-[10px]">CC Budget</p><p className="font-semibold">{ccDetailsByIdLoading ? '…' : fmt(ccBudget)}</p></div>
                    <div className="bg-gray-50 dark:bg-gray-900/40 rounded-xl p-3"><p className="text-gray-400 uppercase font-bold text-[10px]">Allocated So Far</p><p className="font-semibold text-indigo-600">{fmt(subtotal)}</p></div>
                    <div className="bg-gray-50 dark:bg-gray-900/40 rounded-xl p-3"><p className="text-gray-400 uppercase font-bold text-[10px]">Remaining</p><p className={`font-semibold ${ccBudget - subtotal < 0 ? 'text-rose-600' : 'text-emerald-600'}`}>{fmt(ccBudget - subtotal)}</p></div>
                </div>
            )}

            {ccCode && (
                budgetDCADetailsLoading ? (
                    <div className="py-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-indigo-500" /></div>
                ) : budgetDCADetails.length === 0 ? (
                    <EmptyState message="No account heads available for this cost centre type." />
                ) : (
                    <div className="border border-gray-100 dark:border-gray-700 rounded-xl overflow-hidden mb-4">
                        <table className="w-full text-xs">
                            <thead>
                                <tr className="bg-gray-50 dark:bg-gray-900/40 text-left">
                                    <th className="px-3 py-2 font-bold text-gray-500 uppercase">Verify</th>
                                    <th className="px-3 py-2 font-bold text-gray-500 uppercase">Account Head</th>
                                    <th className="px-3 py-2 font-bold text-gray-500 uppercase text-right">Amount</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50 dark:divide-gray-700">
                                {budgetDCADetails.map((d) => {
                                    const r = rowState[d.DCACode] || { amount: '', checked: false };
                                    return (
                                        <tr key={d.DCACode}>
                                            <td className="px-3 py-1.5"><input type="checkbox" checked={r.checked} onChange={(e) => updateRow(d.DCACode, { checked: e.target.checked })} /></td>
                                            <td className="px-3 py-1.5 text-gray-700 dark:text-gray-200">{d.DCACode} — {d.DCAName}</td>
                                            <td className="px-3 py-1.5 text-right">
                                                <input className={`${inputCls} py-1 text-right`} value={r.amount} disabled={!r.checked} onChange={(e) => updateRow(d.DCACode, { amount: e.target.value })} inputMode="decimal" />
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )
            )}

            <Label required>Remarks</Label>
            <textarea rows={2} className={`${inputCls} resize-none`} value={remarks} onChange={(e) => setRemarks(e.target.value)} disabled={dcaSaveLoading} />

            <div className="flex justify-end gap-2 mt-5">
                <Btn variant="secondary" onClick={onClose} disabled={dcaSaveLoading}>Cancel</Btn>
                <Btn onClick={handleSubmit} loading={dcaSaveLoading}>Submit</Btn>
            </div>
        </Modal>
    );
};

// ═════════════════════════════════════════════════════════════════════════════
// AMEND (single) DCA BUDGET MODAL
// ═════════════════════════════════════════════════════════════════════════════

const AmendDCABudgetModal = ({ row, onClose, roleId, availableBalance, availableBalanceLoading, ccBudgetId }) => {
    const dispatch = useDispatch();
    const { singleAmendLoading, singleAmendResult, singleAmendError } = useSelector(selectAssignBudget);
    const [amendType, setAmendType] = useState('');
    const [amount, setAmount] = useState('');

    useEffect(() => {
        if (singleAmendResult) { toast.success('Amendment staged — confirm it below to finalize.'); dispatch(clearSingleAmendResult()); onClose(); }
        if (singleAmendError) { toast.error(singleAmendError); dispatch(clearSingleAmendResult()); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [singleAmendResult, singleAmendError]);

    const dcaBalance = parseFloat(row.DCABudgetBalance) || 0;
    const ccBal = availableBalance || 0;

    const handleSubmit = () => {
        if (!amendType) { toast.warning('Select an Amend Type.'); return; }
        const amt = parseFloat(amount);
        if (!amount || isNaN(amt) || amt <= 0) { toast.warning('Enter a valid Amount.'); return; }
        if (amendType === 'Add' && amt > ccBal) { toast.warning('Amount is greater than the Cost Centre Balance.'); return; }
        if (amendType === 'Substract' && amt > dcaBalance) { toast.warning('Amount is greater than the Account Head Balance.'); return; }

        const newDCABudget = amendType === 'Add' ? (parseFloat(row.DCABudgetValue) || 0) + amt : (parseFloat(row.DCABudgetValue) || 0) - amt;
        const newDCABalance = amendType === 'Add' ? dcaBalance + amt : dcaBalance - amt;
        const newCCBalance = amendType === 'Add' ? ccBal - amt : ccBal + amt;

        dispatch(submitSingleDCAAmend({
            CCCode: row.CCCode,
            DCACode: row.DCACode,
            AmendedValue: amt,
            AmendmentType: amendType,
            DCABudgetId: row.DCABudgetId,
            CCBudgetId: ccBudgetId,
            CCBalance: newCCBalance,
            DCABudget: newDCABudget,
            DCABudgetBalance: newDCABalance,
            FYYear: row.FYyear,
            RoleId: roleId,
        }));
    };

    return (
        <Modal title={`Amend Account Head — ${row.DCACode}`} onClose={onClose}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 text-xs">
                <div className="bg-gray-50 dark:bg-gray-900/40 rounded-xl p-3"><p className="text-gray-400 uppercase font-bold text-[10px]">Account Head</p><p className="font-semibold text-gray-800 dark:text-gray-100">{row.DCAName}</p></div>
                <div className="bg-gray-50 dark:bg-gray-900/40 rounded-xl p-3"><p className="text-gray-400 uppercase font-bold text-[10px]">Head Balance</p><p className="font-semibold">{fmt(dcaBalance)}</p></div>
                <div className="bg-gray-50 dark:bg-gray-900/40 rounded-xl p-3"><p className="text-gray-400 uppercase font-bold text-[10px]">CC Balance Available</p><p className="font-semibold text-indigo-600">{availableBalanceLoading ? '…' : fmt(ccBal)}</p></div>
                <div className="bg-gray-50 dark:bg-gray-900/40 rounded-xl p-3"><p className="text-gray-400 uppercase font-bold text-[10px]">Financial Year</p><p className="font-semibold text-gray-800 dark:text-gray-100">{row.FYyear}</p></div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <Label required>Amend Type</Label>
                    <SelectWrap>
                        <select className={selectCls} value={amendType} onChange={(e) => setAmendType(e.target.value)} disabled={singleAmendLoading}>
                            <option value="">-Select-</option>
                            {AMEND_TYPES.map((t) => <option key={t} value={t}>{t === 'Substract' ? 'Subtract' : t}</option>)}
                        </select>
                    </SelectWrap>
                </div>
                <div>
                    <Label required>Amount</Label>
                    <input className={inputCls} value={amount} onChange={(e) => setAmount(e.target.value)} disabled={singleAmendLoading} inputMode="decimal" />
                </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
                <Btn variant="secondary" onClick={onClose} disabled={singleAmendLoading}>Cancel</Btn>
                <Btn onClick={handleSubmit} loading={singleAmendLoading}>Stage Amendment</Btn>
            </div>
        </Modal>
    );
};

// ═════════════════════════════════════════════════════════════════════════════
// NEW DCA UNDER CC MODAL
// ═════════════════════════════════════════════════════════════════════════════

const NewDCAAmendModal = ({ ccCode, ccTypeDesc, subType, year, onClose, roleId, availableBalance, availableBalanceLoading }) => {
    const dispatch = useDispatch();
    const { unAmendedDCAs, unAmendedDCAsLoading, newDCALoading, newDCAResult, newDCAError } = useSelector(selectAssignBudget);
    const [dcaCode, setDcaCode] = useState('');
    const [amount, setAmount] = useState('');

    useEffect(() => {
        dispatch(fetchUnAmendedDCAs({ CCCode: ccCode, CCtype: ccTypeDesc, SubType: subType, FnYear: year }));
    }, [dispatch, ccCode, ccTypeDesc, subType, year]);

    useEffect(() => {
        if (newDCAResult) { toast.success('New account head budget added successfully.'); dispatch(clearNewDCAResult()); onClose(); }
        if (newDCAError) { toast.error(newDCAError); dispatch(clearNewDCAResult()); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [newDCAResult, newDCAError]);

    const ccBal = availableBalance || 0;

    const handleSubmit = () => {
        if (!dcaCode) { toast.warning('Select an Account Head.'); return; }
        const amt = parseFloat(amount);
        if (!amount || isNaN(amt) || amt <= 0) { toast.warning('Enter a valid Amount.'); return; }
        if (amt > ccBal) { toast.warning('Insufficient Cost Centre Budget Balance.'); return; }

        dispatch(submitNewDCAUnderCC({
            CCCode: ccCode,
            DCABudgetDetails: [{ DCACode: dcaCode, DCABudgetValue: amt }],
            FYyear: year || '',
            RoleId: roleId,
        }));
    };

    return (
        <Modal title="Add New Account Head Under This Cost Centre" onClose={onClose}>
            <div className="mb-4 text-xs bg-gray-50 dark:bg-gray-900/40 rounded-xl p-3">
                <p className="text-gray-400 uppercase font-bold text-[10px]">Cost Centre Balance Available</p>
                <p className="font-semibold text-indigo-600">{availableBalanceLoading ? '…' : fmt(ccBal)}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <Label required>Account Head</Label>
                    <SelectWrap loading={unAmendedDCAsLoading}>
                        <select className={selectCls} value={dcaCode} onChange={(e) => setDcaCode(e.target.value)} disabled={newDCALoading}>
                            <option value="">-Please Select-</option>
                            {unAmendedDCAs.map((d) => <option key={d.DCAID} value={d.DCACode}>{d.DCAIDSTR}</option>)}
                        </select>
                    </SelectWrap>
                </div>
                <div>
                    <Label required>Amount</Label>
                    <input className={inputCls} value={amount} onChange={(e) => setAmount(e.target.value)} disabled={newDCALoading} inputMode="decimal" />
                </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
                <Btn variant="secondary" onClick={onClose} disabled={newDCALoading}>Cancel</Btn>
                <Btn onClick={handleSubmit} loading={newDCALoading}>Submit</Btn>
            </div>
        </Modal>
    );
};

// ═════════════════════════════════════════════════════════════════════════════
// DCA BUDGET TAB
// ═════════════════════════════════════════════════════════════════════════════

const DCABudgetTab = ({ currentUser, roleId, ccTypes, ccTypesLoading, ccFinancialYears, ccSubTypes }) => {
    const dispatch = useDispatch();
    const {
        ccsForDCA, ccsForDCALoading, dcaGrid, dcaGridLoading, dcaGridError,
        pendingAmendments, pendingAmendmentsLoading, confirmAmendLoading, confirmAmendResult, confirmAmendError,
        ccBalance, ccBalanceLoading,
    } = useSelector(selectAssignBudget);

    const [ccTypeId, setCcTypeId] = useState('');
    const [subType, setSubType] = useState('');
    const [year, setYear] = useState('');
    const [ccCode, setCcCode] = useState('');
    const [showAssign, setShowAssign] = useState(false);
    const [showNewDCA, setShowNewDCA] = useState(false);
    const [amendRow, setAmendRow] = useState(null);
    const [checkedAmendIds, setCheckedAmendIds] = useState([]);
    const [confirmRemarks, setConfirmRemarks] = useState('');
    const [deletingAmendId, setDeletingAmendId] = useState(null);

    const ccTypeDesc = ccTypes.find((t) => String(t.CCTypeID) === String(ccTypeId))?.CCTypeDescription || '';
    const isPerforming = ccTypeDesc === 'Performing';

    const handleCcTypeChange = (val) => {
        setCcTypeId(val); setSubType(''); setYear(''); setCcCode('');
        dispatch(clearCCBalance());
    };

    const handleSubTypeChange = (val) => {
        setSubType(val); setCcCode('');
        dispatch(clearCCBalance());
        if (ccTypeId && val) dispatch(fetchCCsForDCA({ CCtypeId: ccTypeId, SubType: val, FnYear: '' }));
    };

    const handleYearChange = (val) => {
        setYear(val); setCcCode('');
        dispatch(clearCCBalance());
        if (ccTypeId && val) dispatch(fetchCCsForDCA({ CCtypeId: ccTypeId, SubType: '', FnYear: val }));
    };

    const runView = () => {
        if (!ccCode) { toast.warning('Select a Cost Centre.'); return; }
        dispatch(fetchDCAGrid({ CCCode: ccCode, Subtype: isPerforming ? subType : '', Year: isPerforming ? '' : year, CCTypeId: parseInt(ccTypeId, 10) || 0 }));
        dispatch(fetchPendingDCAAmendments(ccCode));
        dispatch(fetchCCBalance({ CCCode: ccCode, Year: isPerforming ? '' : year }));
        setCheckedAmendIds([]);
    };

    // spGetBudgetAssignedCCByID already nets every staged (unconfirmed) DCABudgetAmendPendingTransaction
    // row into the returned BalanceBudget server-side, so it already reflects what's available to
    // allocate right now. Netting pendingAmendments again here double-counts every staged amendment.
    const availableCCBalance = useMemo(() => parseFloat(ccBalance?.BalanceBudget) || 0, [ccBalance]);

    useEffect(() => {
        if (confirmAmendResult) {
            toast.success('Amendments confirmed successfully.');
            dispatch(clearConfirmAmendResult());
            setConfirmRemarks('');
            setCheckedAmendIds([]);
            runView();
        }
        if (confirmAmendError) { toast.error(confirmAmendError); dispatch(clearConfirmAmendResult()); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [confirmAmendResult, confirmAmendError]);

    const toggleAmendId = (id) => setCheckedAmendIds((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

    const handleDeleteAmendment = async (p) => {
        setDeletingAmendId(p.PendingTransactionId);
        try {
            await dispatch(deleteSingleDCAAmendment({
                DCABudgetAmendmentId: p.PendingTransactionId,
                AmendmentType: p.AmendmentType,
                AmendedValue: p.AmendedValue,
                DCACode: p.DCACode,
                CCCode: ccCode,
            })).unwrap();
            setCheckedAmendIds((prev) => prev.filter((id) => id !== p.PendingTransactionId));
            dispatch(fetchCCBalance({ CCCode: ccCode, Year: isPerforming ? '' : year }));
        } catch (e) {
            toast.error(typeof e === 'string' ? e : 'Failed to remove staged amendment');
        } finally {
            setDeletingAmendId(null);
        }
    };

    const handleConfirmAmendments = () => {
        if (pendingAmendments.length === 0) { toast.warning('No pending amendments to confirm.'); return; }
        if (checkedAmendIds.length !== pendingAmendments.length) { toast.warning('Verify every pending amendment before confirming.'); return; }
        if (!confirmRemarks.trim()) { toast.warning('Enter Remarks.'); return; }
        dispatch(submitMultipleDCAAmend({ AmendIDs: joinIds(checkedAmendIds), CCCode: ccCode, Remarks: confirmRemarks, RoleId: roleId, CreatedBy: currentUser }));
    };

    return (
        <div className="space-y-4">
            <Card>
                <div className="p-4 flex flex-wrap items-end gap-3">
                    <div className="w-48">
                        <Label>Cost Centre Type</Label>
                        <SelectWrap loading={ccTypesLoading}>
                            <select className={selectCls} value={ccTypeId} onChange={(e) => handleCcTypeChange(e.target.value)}>
                                <option value="">-Please Select-</option>
                                {ccTypes.map((t) => <option key={t.CCTypeID} value={t.CCTypeID}>{t.CCTypeDescription}</option>)}
                            </select>
                        </SelectWrap>
                    </div>
                    {isPerforming ? (
                        <div className="w-48">
                            <Label required>Sub Type</Label>
                            <SelectWrap>
                                <select className={selectCls} value={subType} onChange={(e) => handleSubTypeChange(e.target.value)} disabled={!ccTypeId}>
                                    <option value="">-Please Select-</option>
                                    {ccSubTypes.map((s, i) => <option key={i} value={s.CC_SubType}>{s.CC_SubType}</option>)}
                                </select>
                            </SelectWrap>
                        </div>
                    ) : (
                        <div className="w-48">
                            <Label required>Year</Label>
                            <SelectWrap>
                                <select className={selectCls} value={year} onChange={(e) => handleYearChange(e.target.value)} disabled={!ccTypeId}>
                                    <option value="">-Please Select-</option>
                                    {ccFinancialYears.map((y, i) => <option key={i} value={y.CC_FinYear}>{y.CC_FinYear}</option>)}
                                </select>
                            </SelectWrap>
                        </div>
                    )}
                    <div className="w-56">
                        <Label>Cost Centre</Label>
                        <SelectWrap loading={ccsForDCALoading}>
                            <select className={selectCls} value={ccCode} onChange={(e) => setCcCode(e.target.value)} disabled={!ccTypeId || (isPerforming ? !subType : !year)}>
                                <option value="">-Please Select-</option>
                                {ccsForDCA.map((c) => <option key={c.CC_Id} value={c.CC_Code}>{c.CC_Name}</option>)}
                            </select>
                        </SelectWrap>
                    </div>
                    <Btn onClick={runView} loading={dcaGridLoading} variant="secondary">View</Btn>
                    <div className="ml-auto flex gap-2">
                        {ccCode && <Btn onClick={() => setShowNewDCA(true)} variant="secondary"><PlusCircle className="h-3.5 w-3.5" />Add New Account Head</Btn>}
                        <Btn onClick={() => setShowAssign(true)}><Plus className="h-3.5 w-3.5" />Assign Account Head Budget</Btn>
                    </div>
                </div>
            </Card>

            <Card>
                {dcaGridLoading ? (
                    <div className="py-14 flex justify-center"><Loader2 className="h-6 w-6 text-indigo-500 animate-spin" /></div>
                ) : dcaGridError ? (
                    <div className="py-10 text-center text-sm text-rose-500">{dcaGridError}</div>
                ) : dcaGrid.length === 0 ? (
                    <EmptyState message="No account head budgets found. Select a Cost Centre and click View." />
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-gray-50 dark:bg-gray-900/40 text-left">
                                    {['Account Head', 'Created', 'Budget Value', 'Balance', 'Status', ''].map((h) => (
                                        <th key={h} className="px-3 py-2.5 text-xs font-bold text-gray-500 uppercase tracking-wider">{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50 dark:divide-gray-700">
                                {dcaGrid.map((row, i) => (
                                    <tr key={i} className="hover:bg-indigo-50/40 dark:hover:bg-indigo-900/10 transition-colors">
                                        <td className="px-3 py-2.5 text-gray-800 dark:text-gray-100">{row.DCACode} — {row.DCAName}</td>
                                        <td className="px-3 py-2.5 text-gray-500 text-xs">{row.DCABudgetCreationdate}</td>
                                        <td className="px-3 py-2.5 text-right font-semibold">{fmt(row.DCABudgetValue)}</td>
                                        <td className="px-3 py-2.5 text-right font-semibold text-indigo-600">{fmt(row.DCABudgetBalance)}</td>
                                        <td className="px-3 py-2.5"><Badge color={row.AmendStatus === 'Approved' ? 'green' : row.AmendStatus === 'Rejected' ? 'red' : 'amber'}>{row.AmendStatus}</Badge></td>
                                        <td className="px-3 py-2.5">
                                            {row.AmendStatus === 'Approved' && (
                                                <button onClick={() => setAmendRow(row)} className="p-1.5 rounded-lg text-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-900/30" title="Amend">
                                                    <Pencil className="h-3.5 w-3.5" />
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>

            {ccCode && (
                <Card>
                    <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-indigo-500" />
                        <h2 className="text-sm font-bold text-gray-700 dark:text-gray-200">Pending Amendments — Verify & Confirm</h2>
                    </div>
                    {pendingAmendmentsLoading ? (
                        <div className="py-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-indigo-500" /></div>
                    ) : pendingAmendments.length === 0 ? (
                        <EmptyState message="No staged amendments for this cost centre." />
                    ) : (
                        <div className="p-4 space-y-3">
                            <div className="overflow-x-auto">
                                <table className="w-full text-xs">
                                    <thead>
                                        <tr className="bg-gray-50 dark:bg-gray-900/40 text-left">
                                            <th className="px-3 py-2 font-bold text-gray-500 uppercase">Verify</th>
                                            <th className="px-3 py-2 font-bold text-gray-500 uppercase">Account Head</th>
                                            <th className="px-3 py-2 font-bold text-gray-500 uppercase">Type</th>
                                            <th className="px-3 py-2 font-bold text-gray-500 uppercase text-right">Amount</th>
                                            <th className="px-3 py-2 font-bold text-gray-500 uppercase"></th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-50 dark:divide-gray-700">
                                        {pendingAmendments.map((p) => (
                                            <tr key={p.PendingTransactionId}>
                                                <td className="px-3 py-1.5"><input type="checkbox" checked={checkedAmendIds.includes(p.PendingTransactionId)} onChange={() => toggleAmendId(p.PendingTransactionId)} /></td>
                                                <td className="px-3 py-1.5">{p.DCACode} — {p.DCAName}</td>
                                                <td className="px-3 py-1.5">{p.AmendmentType === 'Substract' ? 'Subtract' : p.AmendmentType}</td>
                                                <td className="px-3 py-1.5 text-right font-semibold">{fmt(p.AmendedValue)}</td>
                                                <td className="px-3 py-1.5 text-right">
                                                    <button
                                                        onClick={() => handleDeleteAmendment(p)}
                                                        disabled={deletingAmendId === p.PendingTransactionId}
                                                        className="p-1.5 rounded-lg text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/30 disabled:opacity-50"
                                                        title="Delete"
                                                    >
                                                        {deletingAmendId === p.PendingTransactionId ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div className="flex items-end gap-3">
                                <div className="flex-1">
                                    <Label required>Remarks</Label>
                                    <input className={inputCls} value={confirmRemarks} onChange={(e) => setConfirmRemarks(e.target.value)} />
                                </div>
                                <Btn onClick={handleConfirmAmendments} loading={confirmAmendLoading}>Confirm Amendments</Btn>
                            </div>
                        </div>
                    )}
                </Card>
            )}

            {showAssign && <AssignDCABudgetModal onClose={() => { setShowAssign(false); if (ccCode) runView(); }} currentUser={currentUser} roleId={roleId} ccTypes={ccTypes} ccFinancialYears={ccFinancialYears} />}
            {showNewDCA && ccCode && (
                <NewDCAAmendModal
                    ccCode={ccCode} ccTypeDesc={ccTypeDesc} subType={isPerforming ? subType : ''} year={isPerforming ? '' : year}
                    onClose={() => { setShowNewDCA(false); runView(); }} roleId={roleId}
                    availableBalance={availableCCBalance} availableBalanceLoading={ccBalanceLoading}
                />
            )}
            {amendRow && (
                <AmendDCABudgetModal
                    row={amendRow} onClose={() => { setAmendRow(null); runView(); }} roleId={roleId}
                    availableBalance={availableCCBalance} availableBalanceLoading={ccBalanceLoading} ccBudgetId={ccBalance?.CCBudgetId}
                />
            )}
        </div>
    );
};

// ═════════════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ═════════════════════════════════════════════════════════════════════════════

const TABS = [
    { id: 'cc', mid: 134, label: 'Cost Centre Budget', icon: Landmark },
    { id: 'dca', mid: 140, label: 'Account Head Budget', icon: Layers },
];

const AssignCCandDCABudget = () => {
    const dispatch = useDispatch();
    const { userData } = useSelector((s) => s.auth);
    const roleId = userData?.roleId || userData?.RID || 0;
    const uid = userData?.UID || userData?.uid || '';
    const currentUser = userData?.userName || userData?.userId || uid || '';

    const { ccTypes, ccTypesLoading, ccFinancialYears, ccSubTypes, workflowStatus } = useSelector(selectAssignBudget);

    const [activeTab, setActiveTab] = useState('');

    useEffect(() => {
        dispatch(fetchCCTypes());
        dispatch(fetchCCFinancialYears());
        dispatch(fetchCCSubTypes());
        dispatch(fetchWorkflowStatus(roleId));
        return () => { dispatch(resetAssignBudget()); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    const visibleTabs = useMemo(
        () => TABS.filter((t) => workflowStatus.find((w) => w.MID === t.mid)?.WorkflowExist),
        [workflowStatus]
    );

    useEffect(() => {
        if (!activeTab && visibleTabs.length > 0) setActiveTab(visibleTabs[0].id);
    }, [activeTab, visibleTabs]);

    const shared = { currentUser, roleId, uid, ccTypes, ccTypesLoading, ccFinancialYears, ccSubTypes };

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors">
            <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-600 px-6 py-8 shadow-lg">
                <div className="max-w-7xl mx-auto flex items-center gap-3">
                    <div className="p-2 bg-white/20 rounded-xl"><Wallet className="h-5 w-5 text-white" /></div>
                    <div>
                        <h1 className="text-xl font-bold text-white">Assign Budget</h1>
                        <p className="text-indigo-200 text-xs mt-0.5">Assign and amend Cost Centre and Account Head budgets</p>
                    </div>
                </div>
            </div>

            <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-sm">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="flex gap-0 overflow-x-auto">
                        {visibleTabs.map(({ id, label, icon: Icon }) => {
                            const active = activeTab === id;
                            return (
                                <button key={id} onClick={() => setActiveTab(id)}
                                    className={`flex items-center gap-2 px-5 py-4 text-sm font-semibold border-b-2 transition-all whitespace-nowrap ${active ? 'border-indigo-600 text-indigo-600 dark:text-indigo-400 dark:border-indigo-400' : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300'}`}>
                                    <Icon className="h-4 w-4" />{label}
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-6 py-6">
                {visibleTabs.length === 0 ? (
                    <EmptyState message="You do not have access to any budget assignment workflow." />
                ) : activeTab === 'cc' ? (
                    <CCBudgetTab {...shared} />
                ) : activeTab === 'dca' ? (
                    <DCABudgetTab {...shared} />
                ) : null}
            </div>
        </div>
    );
};

export default AssignCCandDCABudget;
